import { FormControl, makeStyles, Typography } from "@material-ui/core";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { useCallback, useState } from "react";
import { useMemo } from "react";
import { GetAllChambersQuery } from "../../../graphql/types";

const useStyles = makeStyles((theme: any) => ({
  chamberInput: {
    paddingBottom: "10px",
    [theme.breakpoints.down("sm")]: {
      width: "265px",
      height: "55px",
    },
    [theme.breakpoints.up("sm")]: {
      width: "431px",
      height: "55px",
    },
    borderRadius: "15px",
  },
  selectedValue: {
    backgroundColor: "#F2F2F6",
    borderRadius: "15px",
    [theme.breakpoints.down("sm")]: {
      width: "265px",
      height: "55px",
    },
    [theme.breakpoints.up("sm")]: {
      width: "431px",
      height: "55px",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "black",
        borderRadius: "15px",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#E1A731",
      },
    },
    "& label.Mui-focused": {
      color: "#E1A731",
    },
    "& label.Mui": {
      color: "black",
    },
  },
}));

type ChamberOption = {
  chamberId: string;
  name: string;
};

const ChamberSelector = ({
  chamberId,
  setChamberId,
  allChamberData,
  disabled,
  customLabel,
  additionalChambers,
}: {
  chamberId: string | null;
  setChamberId: any;
  allChamberData: GetAllChambersQuery | undefined;
  disabled?: boolean;
  customLabel?: string;
  additionalChambers?: Array<{ id: string; name: string }>;
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);

  const { chamberIdToChamberName, selectorOptions } = useMemo(() => {
    const chamberIdToChamberName = {} as {
      [key: string]: { chamberId: string; name: string };
    };

    let selectorOptions = [] as ChamberOption[];

    // Filter chambers: non-sandbox and non-hidden
    const filteredChambers =
      allChamberData?.getAllChambers?.filter(
        (chamber) => !chamber.isSandbox && !chamber.hideInApp
      ) || [];

    // Add filtered chambers to the map and options
    for (const chamber of filteredChambers) {
      chamberIdToChamberName[chamber.id] = {
        chamberId: chamber.id,
        name: chamber.name,
      };
    }

    // Convert filtered chambers to options format
    const chamberOptions: ChamberOption[] = filteredChambers.map((chamber) => ({
      chamberId: chamber.id,
      name: chamber.name,
    }));

    // Merge additional chambers if provided
    if (additionalChambers && additionalChambers.length > 0) {
      for (const chamber of additionalChambers) {
        // Only add if not already present
        if (!chamberIdToChamberName[chamber.id]) {
          const option: ChamberOption = {
            chamberId: chamber.id,
            name: chamber.name,
          };
          chamberIdToChamberName[chamber.id] = option;
          chamberOptions.push(option);
        }
      }
    }

    // Alphabetize the combined list
    selectorOptions = chamberOptions.sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    setLoading(false);
    return { chamberIdToChamberName, selectorOptions };
  }, [allChamberData, additionalChambers]);

  const handleChamberChange = useCallback(
    (_, selectedDropdownOption) => {
      setChamberId(
        selectedDropdownOption ? selectedDropdownOption.chamberId : ""
      );
    },
    [setChamberId]
  );

  if (loading) {
    return <></>;
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <FormControl
        className={classes.chamberInput}
        style={{
          marginTop: "10px",
          marginBottom: "20px",
        }}
      >
        <Typography
          style={{
            fontWeight: 600,
            fontSize: "16px",
            marginBottom: "8px",
            color: "#3C3C43",
          }}
        >
          {customLabel || "Select Chamber"}
        </Typography>
        <Autocomplete
          id="chamberSelector"
          disabled={disabled}
          value={chamberId ? chamberIdToChamberName[chamberId] || null : null}
          {...(disabled ? { popupIcon: false } : {})}
          onChange={handleChamberChange}
          className={classes.selectedValue}
          options={selectorOptions}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => <TextField {...params} />}
        />
      </FormControl>
    </div>
  );
};

export default ChamberSelector;
